/**
 * Shared detection + safe messaging for CDN/proxy gateway failures
 * (CloudFront 502/504, ALB 503). These return an HTML error page, never JSON —
 * we must never surface that raw markup to the UI. In a toast the `<!DOCTYPE
 * html>…504…` blob shows as visible tag soup; in the chat bubble ReactMarkdown
 * renders it as literal text. Both are the VP-145 bug.
 *
 * Single source of truth used in two layers (defense in depth):
 *   1. queryClient.throwIfResNotOk — sanitizes at the HTTP boundary.
 *   2. chat surfaces — sanitize again at render time, so a raw body that slips
 *      through any non-apiRequest path still never reaches the user.
 */

const STATUS_PREFIX = /^(\d{3}):\s*/;
// Body whose first non-space chars open an HTML document or a known CDN
// error-page tag (incl. the `<HR>`/`<BR>`/`<PRE>` a CloudFront fragment starts at).
const HTML_DOC_START = /^<(?:!doctype|html|\?xml|head|body|h1|title|address|pre|hr|br)/i;
// Structural close tags that only appear inside a real HTML document.
const HTML_STRUCTURE = /<\/(?:html|body|head)>/i;
// CloudFront/ALB error-page signature lines.
const GATEWAY_SIGNATURE = /generated by cloudfront|request could not be satisfied/i;
const GENERIC_GATEWAY_MESSAGE = "Problem z połączeniem z serwerem. Spróbuj ponownie za chwilę.";

/**
 * Heuristic: does this body look like an HTML document / CDN error page?
 * Three structural signals (any one is enough), all short-circuiting:
 *   1. opens with an HTML/CDN-error tag (full doc or a `<HR>`-led fragment),
 *   2. carries the CloudFront/ALB signature line, or
 *   3. contains an HTML close tag (`</html>` / `</body>` / `</head>`).
 * We deliberately do NOT count arbitrary `<tag>`-shaped substrings — a plain
 * error like "expected <string> got <number>" must stay intact, not be
 * swallowed as a gateway page.
 */
export function looksLikeHtmlBody(raw: string): boolean {
  const head = raw.trimStart().slice(0, 64);
  if (HTML_DOC_START.test(head)) return true;
  if (GATEWAY_SIGNATURE.test(raw)) return true;
  return HTML_STRUCTURE.test(raw);
}

/** Clean, user-safe message for gateway/proxy failures (CloudFront/ALB). */
export function gatewayErrorMessage(status: number): string {
  switch (status) {
    case 502:
      return "Serwer pośredniczący zwrócił błąd (502). Spróbuj ponownie za chwilę.";
    case 503:
      return "Usługa chwilowo niedostępna (503). Spróbuj ponownie za chwilę.";
    case 504:
      return "Przekroczono czas oczekiwania na odpowiedź serwera (504). Operacja mogła trwać zbyt długo — spróbuj ponownie.";
    default:
      return status >= 500
        ? `Błąd serwera (${status}). Spróbuj ponownie za chwilę.`
        : `Błąd żądania (${status}).`;
  }
}

/**
 * Turn any error / error-message into safe display text. If it carries an
 * HTML/gateway blob it is replaced with a friendly message (keyed off the
 * `NNN: ` status prefix when present); otherwise the prefix is stripped and the
 * snippet clamped, so no raw markup or oversized payload ever lands in a toast
 * or chat bubble.
 */
export function sanitizeErrorMessage(input: unknown, maxLen = 300): string {
  const raw = (input instanceof Error ? input.message : String(input ?? "")).trim();
  if (!raw) return GENERIC_GATEWAY_MESSAGE;

  const statusMatch = raw.match(STATUS_PREFIX);
  const status = statusMatch ? Number(statusMatch[1]) : null;
  const body = statusMatch ? raw.slice(statusMatch[0].length) : raw;

  if (looksLikeHtmlBody(body)) {
    return status !== null ? gatewayErrorMessage(status) : GENERIC_GATEWAY_MESSAGE;
  }

  const clamped = body.length > maxLen ? `${body.slice(0, maxLen)}…` : body;
  return clamped || GENERIC_GATEWAY_MESSAGE;
}
